/**
 * Metadata URI probing. Deliberately dependency-free.
 *
 * This lives apart from metadata.ts because that module imports @solana/web3.js
 * and the whole Metaplex umi stack, which in turn pull the native addons
 * bigint-buffer and bufferutil. Loading all of that into the test runner to
 * exercise a function that only does fetch/parse/validate is both slow and
 * platform-fragile -- exactly the class of breakage that a missing Windows
 * native binding already cost this project once. Every other tested module in
 * console/src/lib is pure; this one keeps that property.
 *
 * metadata.ts re-exports probeMetadataUri, so nothing that imported it from
 * there needs to change.
 */

/**
 * A metadata URI that 404s renders as a blank square in every wallet, and if
 * metadata is immutable that is permanent. Checked before launch, not after.
 */
export async function probeMetadataUri(
  uri: string
): Promise<{ ok: boolean; detail: string }> {
  if (!uri.startsWith("https://")) {
    return { ok: true, detail: `${uri.split(":")[0]}:// cannot be fetched here — verify manually` };
  }
  try {
    const res = await fetch(uri, { redirect: "follow" });
    if (!res.ok) return { ok: false, detail: `HTTP ${res.status}` };

    /*
      Read as text first, deliberately. A missing file does NOT reliably 404:
      single-page-application hosts -- including this console's own asset
      worker, and Netlify, GitHub Pages and most S3 static sites -- answer a
      missing path with HTTP 200 and the fallback index.html. Verified against
      a local wrangler dev: GET /meta/does-not-exist.json returned 200 with an
      HTML body and a Content-Type of application/json.

      res.json() on that throws a bare "Unexpected token <", which sends the
      operator hunting for a JSON syntax error in a file that does not exist.
      Naming it is the whole point of testing the URI before the mint.
    */
    const body = await res.text();
    const head = body.trimStart().slice(0, 1);
    if (head === "<") {
      return {
        ok: false,
        detail:
          "the host returned an HTML page, not JSON (HTTP 200). The file almost " +
          "certainly does not exist -- many static hosts serve a fallback page " +
          "instead of a 404. Check the path.",
      };
    }

    let json: Record<string, unknown>;
    try {
      json = JSON.parse(body) as Record<string, unknown>;
    } catch {
      return { ok: false, detail: `the response is not valid JSON (${body.length} bytes)` };
    }

    if (typeof json.name !== "string" || typeof json.image !== "string") {
      return { ok: false, detail: 'JSON is missing a "name" or "image" field' };
    }

    /*
      A wallet fetches this cross-origin from a browser. A host that serves the
      JSON happily to this probe but sends no CORS header renders the token as
      a blank square -- permanently, because the URI is on chain by then and
      this project revokes update authority. Warn, do not block: the header is
      not always visible to fetch(), so absence is a suspicion, not a verdict.
    */
    const acao = res.headers.get("access-control-allow-origin");
    const cors = acao ? "" : " -- WARNING: no Access-Control-Allow-Origin seen; a wallet may not be able to read it";

    return { ok: true, detail: `HTTP 200, name="${json.name}"${cors}` };
  } catch (e) {
    return { ok: false, detail: (e as Error).message };
  }
}
